import { useMutation, useQuery } from '@tanstack/react-query'

import type {
  McpAssistantConnection,
  McpConnectionResponse,
  McpUploadLinkDetail,
  McpUploadLinkResult,
} from '@brickup/shared/dto'
import { COMMON } from '@brickup/shared/locales/de/common'
import { apiClient, publicClient } from './client'
import type { GlanceOptions } from './glance'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

const BASE = '/internal/system/mcp'
const CONNECTIONS_KEY = ['mcp-connections']

export type McpConsentRequest = {
  clientName: string
  scopes: string[]
  allScopes: string[]
}

/** What the assistant is asking for. Public: the browser arrives here from the
 *  client's redirect, before anyone has signed in. */
export function useMcpConsent(request: string) {
  return useQuery({
    queryKey: ['mcp-consent', request],
    retry: false,
    queryFn: async (): Promise<McpConsentRequest> => {
      const { data } = await publicClient.get<McpConsentRequest>(
        '/mcp/consent',
        { params: { request } },
      )
      return data
    },
  })
}

/** Grants it, as the signed-in operator. Answers with where to send the
 *  browser next — back to the client, carrying the code. */
export function useGrantMcpConsent() {
  return useMutation({
    mutationFn: async (request: string) => {
      const { data } = await apiClient.post<{ redirectTo: string }>(
        '/mcp/consent',
        { request },
      )
      return data
    },
  })
}

/** The address an MCP client connects to. It only changes when the backend
 *  is deployed. */
export function useMcpConnection() {
  return useQuery({
    queryKey: ['mcp-connection'],
    staleTime: Infinity,
    queryFn: async (): Promise<McpConnectionResponse> => {
      const { data } = await apiClient.get<McpConnectionResponse>(BASE)
      return data
    },
  })
}

/** Which assistants are reaching the app right now, one row per account and
 *  client. */
export function useMcpConnections(options?: GlanceOptions) {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: async (): Promise<McpAssistantConnection[]> => {
      const { data } = await apiClient.get<McpAssistantConnection[]>(
        `${BASE}/connections`,
      )
      return data
    },
    ...options,
  })
}

/** Cuts one account's connection to one client. The server checks the caller
 *  runs that account; the screen offers the button on the same rule. */
export function useRevokeMcpConnection() {
  return useMutationWithInvalidation(
    ({ userId, clientId }: { userId: number; clientId: number }) =>
      apiClient.delete(`${BASE}/connections/${userId}/${clientId}`),
    [CONNECTIONS_KEY],
    { errorFallback: COMMON.errors.deleteFailed },
  )
}

/** What the page an assistant prepared is for. */
export function useMcpUploadLink(token: string) {
  return useQuery({
    queryKey: ['mcp-upload-link', token],
    retry: false,
    queryFn: async (): Promise<McpUploadLinkDetail> => {
      const { data } = await apiClient.get<McpUploadLinkDetail>(
        `/mcp/upload-link/${token}`,
      )
      return data
    },
  })
}

/** The one multipart request in the whole flow: the picture itself. */
export function useRedeemMcpUploadLink(token: string) {
  return useMutation({
    mutationFn: async (file: File): Promise<McpUploadLinkResult> => {
      const body = new FormData()
      body.append('file', file)
      const { data } = await apiClient.post<McpUploadLinkResult>(
        `/mcp/upload-link/${token}`,
        body,
      )
      return data
    },
  })
}
